/**
 * What the search box sends. Nine digits are an EIN and go straight to a page
 * without asking; anything else is folded with the matcher's own `tokens`,
 * the fold the index was built with, so an accent, an article, or an
 * abbreviation typed by the reader meets the index's spelling. A possessive
 * is dropped first, because the matcher keeps its S ("ROSIES") and a filed
 * name may not ("ST JUDE"): a prefix without the S matches both. Fewer than
 * two characters once folded asks nothing.
 */
import { SUFFIXES, tokens } from '../../../src/matcher/normalize.js';

export type QueryPlan = { readonly kind: 'none' } | { readonly kind: 'ein'; readonly ein: string } | { readonly kind: 'search'; readonly q: string };

export const SEARCH_DEBOUNCE_MS = 150;
const QUERY_LIMIT = 100;
const EIN_LENGTH = 9;

const POSSESSIVE = /['’]s\b/gi;
const RETRY_MINIMUM_LETTERS = 4;

/**
 * The same query with a trailing S dropped from every token of four letters or more, for one retry
 * after an empty answer: "St Judes" typed without its apostrophe finds "ST JUDE" on the second ask,
 * and "Davis" is never retried as "Davi" unless it found nothing. Null when nothing would change.
 */
export const withoutTrailingS = (q: string): string | null => {
  const retried = q
    .split(' ')
    .map((token) => (token.length >= RETRY_MINIMUM_LETTERS && token.endsWith('S') ? token.slice(0, -1) : token))
    .join(' ');
  return retried === q ? null : retried;
};

export const planQuery = (input: string): QueryPlan => {
  const trimmed = input.replace(POSSESSIVE, '').trim();
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length === EIN_LENGTH && /^[0-9-]+$/.test(trimmed)) {
    return { kind: 'ein', ein: digits };
  }
  const words = tokens(trimmed);
  // `tokens` keeps an article or a legal form when nothing else is left, so an organization named only
  // "The" can still be matched; a reader who has typed only "the" has not named anything yet.
  if (words.every((word) => SUFFIXES.has(word))) {
    return { kind: 'none' };
  }
  const q = words.join(' ').slice(0, QUERY_LIMIT);
  return q.length < 2 ? { kind: 'none' } : { kind: 'search', q };
};
